import {
  Button,
  FileInput,
  LoadingOverlay,
  NumberInput,
  Paper,
  Select,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { ArrowLeft } from "lucide-react";
import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useAddLiveChannel, useUpdateLiveChannel } from "../../utils/mutations";
import { useLiveChannelById } from "../../utils/queries";

const AddLiveChannel = () => {
  const params = useParams();
  const id = params.id;

  const form = useForm({
    initialValues: {
      name: "",
      streamUrl: "",
      isEnable: "true",
      position: 1,
      logo: null,
    },
    validate: {
      name: (v) => (v?.trim() ? null : "Name is required"),
      streamUrl: (v) => (v?.trim() ? null : "Stream URL is required"),
      logo: (v) => (!id && !v ? "Logo is required for new channels" : null),
    },
  });

  const { isPending, mutate } = useAddLiveChannel();
  const { isPending: isUpdating, mutate: update } = useUpdateLiveChannel();
  const { data, isLoading } = useLiveChannelById(id);

  useEffect(() => {
    if (data) {
      const channel = data?.data || data;
      form.setValues({
        name: channel?.name ?? "",
        streamUrl: channel?.streamUrl ?? "",
        isEnable: channel?.isEnable ? "true" : "false",
        position: channel?.position ?? 1,
      });
    }
  }, [data]);

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (values.logo) formData.append("logo", values.logo);
    formData.append("name", values.name);
    formData.append("streamUrl", values.streamUrl);
    formData.append("isEnable", values.isEnable === "true");
    formData.append("position", values.position);

    if (id) update({ id, data: formData });
    else mutate(formData);
  };

  return (
    <div className="form-page">
      <Link to="/live-channels" className="form-page-back">
        <ArrowLeft size={18} />
        Back to Live Channels
      </Link>
      <Title order={2} className="form-page-title">
        {id ? "Edit Live Channel" : "Add Live Channel"}
      </Title>

      <Paper className="form-card" radius="md" p="xl">
        <LoadingOverlay visible={isLoading} />
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <div className="form-card-section">
            <Text size="sm" fw={600} c="dimmed" mb="sm" tt="uppercase">
              Channel Details
            </Text>
            <TextInput
              label="Channel Name"
              placeholder="Enter channel name"
              {...form.getInputProps("name")}
              mb="md"
            />
            <TextInput
              label="Stream URL"
              placeholder="Enter stream URL"
              {...form.getInputProps("streamUrl")}
              mb="md"
            />
            <NumberInput
              label="Position"
              placeholder="Enter position"
              {...form.getInputProps("position")}
              min={1}
              mb="md"
            />
            <Select
              label="Status"
              placeholder="Select status"
              data={[
                { value: "true", label: "Enabled" },
                { value: "false", label: "Disabled" },
              ]}
              {...form.getInputProps("isEnable")}
              mb="md"
            />
          </div>

          <div className="form-card-section">
            <Text size="sm" fw={600} c="dimmed" mb="sm" tt="uppercase">
              Logo
            </Text>
            <FileInput
              label={id ? "Logo (leave empty to keep existing)" : "Logo"}
              placeholder="Click to upload"
              accept="image/*"
              {...form.getInputProps("logo")}
              mb="md"
            />
          </div>

          <div className="form-card-actions">
            <Button type="submit" loading={isPending || isUpdating} size="md">
              {id ? "Update Channel" : "Add Channel"}
            </Button>
            <Button component={Link} to="/live-channels" variant="default">
              Cancel
            </Button>
          </div>
        </form>
      </Paper>
    </div>
  );
};

export default AddLiveChannel;
